import { merge } from 'lodash';
import { ObjectUtils } from '../../_core/utils/utils.object';
import { PublicBusiness } from '../../business/business.entity';
import { CreativeUnitPackageDto } from '../models/creative-unit-package.dto';
import { CustomizationUtils } from './customization.utils';
import { LayersUtils } from './layers.utils';
import { CreativeUnitWithCustomizationsDto } from '../models/creative-unit/creative-unit.dto';
import { MergeTags } from '../../_core/utils/utils.merge-tags';
import { ProjectCartItemGroupDto } from '../../cart/models/project-cart-item-group.dto';

export class MergePropertiesUtils {
	/**
	 * Creates a state of all important package details to be used to easily traverse
	 * the state and find properties to use for merge tags.
	 */
	public static getMergeableProperties(pkg: CreativeUnitPackageDto, business?: PublicBusiness, unit?: CreativeUnitWithCustomizationsDto, activeGroup?: ProjectCartItemGroupDto) {
		// pkg = ObjectUtils.unfreeze(pkg);
		// if(business) {
		// 	business = ObjectUtils.unfreeze(business);
		// }
		// if(unit) {
		// 	unit = ObjectUtils.unfreeze(unit);
		// }
		let customizations = CustomizationUtils.findAllCustomizations(unit?.customizations  || activeGroup?.customizations || pkg?.customizations || []);

		const state: any = {
			customizations: customizations.reduce((acc, customization) => {
				acc[customization.id] = customization;
				return acc;
			}, {}),
			layers: LayersUtils.flattenLayers(unit?.layers || pkg?.layers || []).reduce((acc, layer) => {
				acc[layer.id] = layer;
				return acc;
			}, {}),
			variables: {},
			business: business || {},
			activeCreativeUnit: unit,
			activeGroup: activeGroup || {},
			scripts: pkg?.scripts || unit?.scripts || []
		};

		// console.log('State', state);

		// Iterate through each variable and try to apply merge tags to them.
		// This allows us to have downstream variables that are dependent on upstream variables.
		state.variables = this.addVariablesToStateRecursively(
			ObjectUtils.unfreeze(unit?.variables || pkg?.variables || {}),
			state
		);
		// console.log('Merged Variables', state?.variables);

		// Creative Unit variable overrides should override even customization changes.
		// If there is no unit, we are likely in an all tab/group context, so we should apply the active group's variable overrides.
		// console.log('Checking for variable overrides', unit.variables, unit.variableOverrides);
		if (unit?.variableOverrides || (!unit && activeGroup?.variableOverrides)) {
			const stateVariables = state.variables;
			const overrideVariables = ObjectUtils.unfreeze(!unit ? activeGroup?.variableOverrides : unit?.variableOverrides);

			// Iterate through each variable and try to apply merge tags to them.
			// This allows us to have downstream variables that are dependent on upstream variables.
			try {
				for (let key in overrideVariables) {
					let value = overrideVariables[key];
					if (typeof value === 'string') {
						overrideVariables[key] = MergeTags.applyMergeTagsToString(value, state);
					}
				}
			} catch (e) {
				console.log('Error applying merge tags to variables', e);
			}

			let mergedVariables = merge(stateVariables, overrideVariables);
			state.variables = this.addVariablesToStateRecursively(mergedVariables, state);
			// console.log('Merged Variables after Creative unit overrides', state?.variables);
			// console.log('Merged Variables', unit.variables);
		}

		// Merge customization value variables into the state.
		for (let customization of customizations) {

			// Test for visibility.  We do this inside the loop so that we can retest if the customization ahead of us changed something that now
			// makes this customization visible.

			// Test if the customization parent is visible.
			if (customization.parentId) {
				let parentCustomization = customizations.find(c => c.id === customization.parentId);
				if (parentCustomization && !CustomizationUtils.isCustomizationVisible(parentCustomization, undefined, state, activeGroup)) {
					continue;
				}
			}

			// Test if the customization is visible.
			if (!CustomizationUtils.isCustomizationVisible(customization, undefined, state, activeGroup)) {
				continue;
			}

			let variables = CustomizationUtils.getCustomizationVariables(customization);

			if (variables) {
				const stateVariables = state.variables;
				const mergedVariables = merge(stateVariables, variables);
				state.variables = this.addVariablesToStateRecursively(mergedVariables, state);

				// console.log('Merged Variables after customizations', state?.variables);
			}
		}

		// Now look for creative unit specific overrides.
		if (unit) {
			for (let customization of customizations) {

				// Test for visibility.  We do this inside the loop so that we can retest if the customization ahead of us changed something that now
				// makes this customization visible.

				// Test if the customization parent is visible.
				if (customization.parentId) {
					let parentCustomization = customizations.find(c => c.id === customization.parentId);
					if (parentCustomization && !CustomizationUtils.isCustomizationVisible(parentCustomization, undefined, state)) {
						continue;
					}
				}

				// Test if the customization is visible.
				if (!CustomizationUtils.isCustomizationVisible(customization, undefined, state)) {
					continue;
				}

				let variables = CustomizationUtils.getCustomizationUnitVariables(customization, unit);
				// console.log('Customization Unit Variables', customization.label, variables, customization);

				if (variables) {
					const stateVariables = state.variables;
					const mergedVariables = merge(stateVariables, variables);
					state.variables = this.addVariablesToStateRecursively(mergedVariables, state);

					// console.log('Merged Variables after customizations creative unit overrides', state?.variables);
				} else {
					// console.log('No variables found for customization', customization);
				}
			}
		}

		return state;
	}

	// Iterate through each variable and try to apply merge tags to them.
	// This allows us to have downstream variables that are dependent on upstream variables.
	public static addVariablesToStateRecursively(variables: any, state: any): any {
		const output: any = {};
		for (const key in variables) {
			if (!variables.hasOwnProperty(key)) {
				continue;
			}

			const value = variables[key];
			if (typeof value === 'string') {
				try {
					output[key] = state.variables[key] = MergeTags.applyMergeTagsToString(value, state);
				} catch (e) {
					console.warn('Error applying merge tags to variables', e);
				}
			} else if (typeof value === 'object' && value !== null) {
				output[key] = state.variables[key] = this.addVariablesToStateRecursively(value, state);
			} else {
				output[key] = state.variables[key] = value; // Fallback for primitive values
			}
		}
		return output;
	}

	public static getMergedParams(params: any, mergeTagState: any) {
		return JSON.parse(MergeTags.applyMergeTagsToString(JSON.stringify(params || {}), mergeTagState));
	}
}
